import { Component, ChangeDetectionStrategy, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { Auth } from 'src/providers/auth/auth';
import { Client } from 'src/providers/client/client';
import { Me } from 'src/providers/me/me';
import { Events } from 'src/providers/events/events';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { RecoverAccountService } from 'src/providers/recover-account/recover-account';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { SocialAuthService, SocialAuthResult } from 'src/shared/services/social-auth.service';
import { GuestAccountService } from 'src/shared/services/guest-account.service';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { PosthogService } from 'src/shared/services/posthog.service';
import { BookService } from 'src/shared/services/book.service';

import { Util } from 'src/shared/util/util';
import { validateEmail } from 'src/shared/validator/validateEmail';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

type Step = 'landing' | 'account-type' | 'signin' | 'reset-password' | 'forgot-username';
type AccountTypeValue = 'regular' | 'rental' | 'company' | 'pro';

interface AccountTypeOption {
  value: AccountTypeValue;
  label: string;
  icon: string;
}

// Full-page x.com-style landing/auth experience shown to native iOS/Android users
// at the root route while unauthenticated. Mobile web and desktop keep the modal.
@Component({
  selector: 'app-native-landing',
  templateUrl: './native-landing.page.html',
  styleUrls: ['./native-landing.page.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NativeLandingPage implements OnInit {
  private fb = inject(FormBuilder);
  private auth = inject(Auth);
  private client = inject(Client);
  private me = inject(Me);
  private events = inject(Events);
  private onboardingProvider = inject(OnboardingProvider);
  private recoverAccountService = inject(RecoverAccountService);
  private navCtrl = inject(CustomNavController);
  private storage = inject(TidyStorage);
  private socialAuth = inject(SocialAuthService);
  private guestAccountService = inject(GuestAccountService);
  private authModalService = inject(AuthModalService);
  private addressesQueries = inject(AddressesQueriesService);
  private posthogService = inject(PosthogService);
  private bookService = inject(BookService);
  private util = inject(Util);

  readonly accountTypes: AccountTypeOption[] = [
    { value: 'rental', label: 'For Property Managers', icon: 'apartment' },
    { value: 'regular', label: 'For My Home', icon: 'home' },
    { value: 'pro', label: 'For Service Pros', icon: 'handyman' },
    { value: 'company', label: 'For the Office', icon: 'business' }
  ];

  readonly step = signal<Step>('landing');
  readonly isLoading = signal(false);
  readonly loadingType = signal<AccountTypeValue | null>(null);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly submitted = signal(false);

  // True when the social auth backend call reported a brand-new account and
  // we still need the user to pick an account type before going to dashboard.
  readonly isNewSocialUser = signal(false);

  readonly signInForm = this.fb.group({
    email: ['', [Validators.required, validateEmail]],
    password: ['', Validators.required]
  });
  readonly resetPasswordForm = this.fb.group({
    email: ['', [Validators.required, validateEmail]]
  });
  readonly forgotUsernameForm = this.fb.group({
    phone: ['', Validators.required],
    address1: ['', Validators.required],
    zipcode: ['', Validators.required]
  });

  async ngOnInit(): Promise<void> {
    // Authenticated real accounts shouldn't see this page, but guests may —
    // e.g. when they back-navigate to the start of their guest session.
    if (this.auth.isAuthenticated()) {
      const isGuest = await this.storage.retrieve('isGuestAccount');
      if (!isGuest) {
        this.navCtrl.navigateRoot('properties');
      }
    }
  }

  // ---- landing step ----

  signUpWithGoogle = (): Promise<void> =>
    this.runSocialSignup(() => this.socialAuth.signInWithGoogle(), 'Google');

  signUpWithApple = (): Promise<void> =>
    this.runSocialSignup(() => this.socialAuth.signInWithApple(), 'Apple');

  goToCreateAccount = (): void => {
    this.isNewSocialUser.set(false);
    this.errorMessage.set('');
    this.step.set('account-type');
  };

  goToSignIn = (): void => {
    this.errorMessage.set('');
    this.submitted.set(false);
    this.step.set('signin');
  };

  openUrl(url: string): void {
    this.util.openUrl(url);
  }

  // ---- account-type step ----

  selectAccountType = async (type: AccountTypeValue): Promise<void> => {
    if (this.loadingType()) {
      return;
    }
    if (type === 'pro') {
      // Service providers use the separate TIDY for Pros app — no account created here.
      this.util.openUrl('https://www.tidy.com/pros');
      return;
    }
    this.loadingType.set(type);
    this.errorMessage.set('');
    try {
      if (this.isNewSocialUser()) {
        await this.applySocialAccountType(type);
      } else {
        await this.createGuestAndContinue(type);
      }
    } catch (error) {
      console.error('Native landing sign up failed:', error);
      this.util.showError('Something went wrong. Please try again.');
      this.errorMessage.set('Something went wrong. Please try again.');
    } finally {
      this.loadingType.set(null);
    }
  };

  // ---- signin / recovery steps ----

  onSignIn = async (): Promise<void> => {
    this.submitted.set(true);
    this.errorMessage.set('');
    if (!this.signInForm.valid) {
      return;
    }
    this.isLoading.set(true);
    try {
      const credentials = {
        username: this.signInForm.value.email,
        password: this.signInForm.value.password
      };
      await this.auth.login(credentials, 'login', null);
      const token = localStorage.getItem('tidy_token');
      const tokenType = localStorage.getItem('tidy_token_type');
      // auth.login already flipped `authenticated` to true. The TanStack queries gated by that
      // signal (subscriptions, workflows, concierge) would fire during the clear window below,
      // go out without a bearer, get a 401 and trigger the global logout-on-401 — bouncing the
      // user back to this landing page (or stranding them on a loading screen). Hide the session
      // until the token is back in storage. Social login avoids this by only setting the signal
      // after the token is restored.
      this.auth.authenticated.set(false);
      this.clearLocalStoragePreservingPosthog();
      await this.storage.clear();
      localStorage.setItem('tidy_token', token ?? '');
      localStorage.setItem('tidy_token_type', tokenType ?? '');
      this.auth.authenticated.set(true);
      await this.storage.save('didJustSignIn', true);
      await this.storage.save('isGuestAccount', false);
      const account = await this.me.load(true);
      await this.onboardingProvider.getGoalsFromServer();
      this.events.publish('goals:updated');
      await this.addressesQueries.getAddresses();
      await this.afterAuthentication(account);
      this.navCtrl.navigateRoot('properties');
    } catch (err: any) {
      this.isLoading.set(false);
      if (err?.err?.has_homekeeper_account) {
        this.errorMessage.set('This account is for service providers. Please use the TIDY for Pros app.');
      } else {
        this.errorMessage.set('Invalid email or password. Please try again.');
      }
    }
  };

  onResetPassword = async (): Promise<void> => {
    this.submitted.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    if (!this.resetPasswordForm.valid) {
      return;
    }
    this.isLoading.set(true);
    try {
      await this.auth.recoverPassword({ email: this.resetPasswordForm.value.email });
      this.isLoading.set(false);
      this.successMessage.set('Password reset instructions have been sent to your email.');
      setTimeout(() => this.goBackToSignIn(), 3000);
    } catch (err: any) {
      this.isLoading.set(false);
      if (err?.status === 404) {
        this.errorMessage.set('No account found with this email address.');
      } else {
        this.errorMessage.set('Failed to send reset email. Please try again.');
      }
    }
  };

  onForgotUsername = async (): Promise<void> => {
    this.submitted.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    if (!this.forgotUsernameForm.valid) {
      return;
    }
    this.isLoading.set(true);
    try {
      const params = {
        phone: this.forgotUsernameForm.value.phone,
        address: {
          address1: this.forgotUsernameForm.value.address1,
          zipcode: this.forgotUsernameForm.value.zipcode
        }
      };
      const response: any = await this.recoverAccountService.recoverUserName(params as any);
      this.isLoading.set(false);
      this.successMessage.set(
        response?.email ? `Your username is: ${response.email}` : 'Username recovery information has been sent.'
      );
      setTimeout(() => this.goBackToSignIn(), 5000);
    } catch (err: any) {
      this.isLoading.set(false);
      if (err?.status === 404) {
        this.errorMessage.set('No account found with the provided information.');
      } else {
        this.errorMessage.set('Failed to recover username. Please try again.');
      }
    }
  };

  forgotPassword = (): void => {
    this.errorMessage.set('');
    this.successMessage.set('');
    this.submitted.set(false);
    this.resetPasswordForm.reset();
    this.step.set('reset-password');
  };

  forgotUsername = (): void => {
    this.errorMessage.set('');
    this.successMessage.set('');
    this.submitted.set(false);
    this.forgotUsernameForm.reset();
    this.step.set('forgot-username');
  };

  // ---- navigation ----

  goBack = (): void => {
    const current = this.step();
    if (current === 'account-type' && this.isNewSocialUser()) {
      // Account is already created via social auth — accept the default
      // backend type rather than dropping back to an unauthed landing page.
      this.isNewSocialUser.set(false);
      this.navCtrl.navigateRoot('properties');
      return;
    }
    if (current === 'account-type' || current === 'signin') {
      this.isNewSocialUser.set(false);
      this.step.set('landing');
    } else if (current === 'reset-password' || current === 'forgot-username') {
      this.step.set('signin');
    }
    this.errorMessage.set('');
    this.successMessage.set('');
    this.submitted.set(false);
  };

  private goBackToSignIn(): void {
    this.step.set('signin');
    this.errorMessage.set('');
    this.successMessage.set('');
    this.submitted.set(false);
  }

  // ---- internal helpers ----

  private async runSocialSignup(authFn: () => Promise<SocialAuthResult>, provider: string): Promise<void> {
    if (this.isLoading()) {
      return;
    }
    this.isLoading.set(true);
    this.errorMessage.set('');
    try {
      const authResult = await authFn();
      const response = await this.socialAuth.authenticateWithBackend(authResult);
      this.clearLocalStoragePreservingPosthog();
      await this.storage.clear();
      localStorage.setItem('tidy_token', response.access_token);
      localStorage.setItem('tidy_token_type', response.token_type);
      this.auth.authenticated.set(true);
      await this.storage.save('didJustSignIn', true);
      await this.storage.save('isGuestAccount', false);

      if (response.is_new_user) {
        // Brand-new account — collect the account type before going to dashboard.
        this.isNewSocialUser.set(true);
        this.step.set('account-type');
      } else {
        // Existing TIDY account — sign them in directly, no account-type screen.
        const account = await this.me.load(true);
        await this.afterAuthentication(account);
        this.navCtrl.navigateRoot('properties');
      }
    } catch (error: any) {
      this.handleSocialAuthError(error, provider);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async applySocialAccountType(type: AccountTypeValue): Promise<void> {
    await this.client.saveClientSettings({ profile: { account_type: type } });
    const account = await this.me.load(true);
    await this.afterAuthentication(account);
    this.isNewSocialUser.set(false);
    this.navCtrl.navigateRoot('properties');
  }

  private async createGuestAndContinue(type: AccountTypeValue): Promise<void> {
    await this.guestAccountService.completeGuestSetup(type);
    const variant = await this.authModalService.resolveGuestLandingVariant();
    this.navCtrl.navigateRoot(variant === 'chat' ? 'chat' : 'properties');
  }

  private async afterAuthentication(account: any): Promise<void> {
    if (account?.customer_account?.account_type) {
      this.auth.setAccountTypeStorage(account.customer_account.account_type);
    }
    this.auth.authenticated.set(true);
    this.auth.setSession();
    if (account?.customer_account?.id && account?.customer_account?.email) {
      await this.posthogService.identify(
        account.customer_account.id,
        account.customer_account.email,
        { account_type: account.customer_account.account_type }
      );
      await this.posthogService.reloadFeatureFlags();
      await this.bookService.cacheBookingViewV2Variant();
    }
    this.events.publish('auth:complete');
  }

  private handleSocialAuthError(error: any, provider: string): void {
    const message = error?.message || error?.error?.message || '';
    if (message.includes('canceled') || message.includes('cancelled') || message.includes('popup_closed')) {
      return;
    }
    if (error?.status === 409) {
      this.errorMessage.set('An account with this email already exists. Please sign in instead.');
    } else if (error?.error?.has_homekeeper_account) {
      this.errorMessage.set('This account is for service providers. Please use the TIDY for Pros app.');
    } else {
      this.errorMessage.set(`${provider} sign-in failed. Please try again.`);
    }
  }

  private clearLocalStoragePreservingPosthog(): void {
    const preserved: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('ph_') || key?.startsWith('tidy_theme')) {
        preserved.push([key, localStorage.getItem(key)!]);
      }
    }
    localStorage.clear();
    preserved.forEach(([k, v]) => localStorage.setItem(k, v));
  }
}
